import { AdSize, ExtendedFormatSettings } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { filterDuplicates, getBrowserDetails, isNotUndefined, isNumber, isString, isStringArray } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onModuleRegistration } from '../../../loader/decorators/on-module-registration.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectCommonSettings, selectFormats } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { CONFIG_RESOLVER } from '../../../loader/token.js';
import { filterDuplicateSizes, filterSizes, isAdSizes } from '../../../utils/adsize-helper.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { FormatMaster } from './format-master.js';

const log = logger.logGroup({ prefix: 'FormatController' });

export interface ViewportBucket {
    name: string;
    minWidth: number;
    maxWidth: number;
}

interface FormatTargeting {
    af: string[];
    bf: string[];
    sizes: AdSize[];
}

@injectionTarget()
export class FormatController {

    @inject(CONFIG_RESOLVER) private configResolver!: ConfigResolver;

    private formats: Record<string, FormatMaster> = {};

    /**
     * Creates a `FormatMaster` for each format listed in the config.
     */
    @onModuleRegistration()
    public init() {
        const formatConfig = this.configResolver.get(selectFormats()) || {};

        for (const [formatName, formatSettings] of Object.entries(formatConfig)) {
            this.formats[formatName] = new FormatMaster(this, formatName, { ...formatSettings });
        }
    }

    /**
     * get a format based on given name. Can be used to read settings from the format or to block it.
     * @param formatName
     */
    public getFormat(formatName: string): FormatMaster | undefined {
        return this.formats[formatName];
    }

    /**
     * edit a given format, overwriting their current settings with new ones.
     * Warning: setting values will not trigger changes or behaviour yet (like re-evaluating blocked status)
     * @param formatName
     * @param newFormatSettings
     */
    public editFormat(formatName: string, newFormatSettings: Partial<ExtendedFormatSettings>): void {
        const currentFormat = this.getFormat(formatName);
        if (!currentFormat) return;

        currentFormat.settings = {
            ...currentFormat.settings,
            ...newFormatSettings
        };
    }

    public getFormatTargeting(adslot: Adslot): FormatTargeting {
        // get additional sizes and kvPreset for the adslot defined in the slot settings
        const { dfpSizes: adslotAdditionalSizes = [], kvPreset = {} } = adslot.getPositionSettings();
        // get blocked sizes for the adslot provided by the publisher
        const adslotBlockedSizes = adslot.getBlockedSizes();

        const { isPhone } = getBrowserDetails();

        const availableFormats = Object.values(this.formats).filter(format => format.isActive());
        const blockedFormats = availableFormats.filter(format => format.isBlockedByViewport() || format.isBlockedByZone(adslot.getZone()) || format.isBlockedByPublisher(adslot));
        const sizesFormats = availableFormats.filter(format => !blockedFormats.includes(format));

        return {
            af: availableFormats
                .map(format => format.getIdentForSlotname(adslot.slotName))
                .concat(isStringArray(kvPreset.af) ? kvPreset.af : [])
                .filter(filterDuplicates)
                .filter(isNotUndefined),
            bf: blockedFormats
                .map(format => format.getIdentForSlotname(adslot.slotName))
                .concat(isStringArray(kvPreset.bf) ? kvPreset.bf : [])
                .filter(filterDuplicates)
                .filter(isNotUndefined),
            sizes: sizesFormats
                .map(format => format.constructSizesPerSlotname(isPhone)) // construct sizes for each format; due to countSizesUp we need to generate all
                .flatMap(sizesPerSlotname => sizesPerSlotname[adslot.slotName] ?? []) // extract sizes for specific slotName
                .concat(isAdSizes(adslotAdditionalSizes) ? adslotAdditionalSizes : []) // add additional sizes from slot settings
                .filter(filterDuplicateSizes) // remove duplicates which can occur when using additional sizes
                .filter(filterSizes(adslotBlockedSizes)), // remove blocked sizes the publisher provided
        }
    }

    public getViewportBuckets(): ViewportBucket[] {
        // TODO useMemo
        const { viewportBuckets = [] } = this.configResolver.get(selectCommonSettings());
        return viewportBuckets
            .filter((b) => isString(b.name) && isNumber(b.minWidth))
            .sort((b1, b2) => b1.minWidth - b2.minWidth)
            .map(({ name, minWidth }, index, arr) => ({ name, minWidth, maxWidth: (arr[index + 1]?.minWidth ?? 1e4) - 1 }))
            .filter((b) => b.minWidth < b.maxWidth);
    }

    public blockFormats(blockedFormats: string[]): void {
        for (const [name, formatMaster] of Object.entries(this.formats)) {
            if (blockedFormats.includes(name)) {
                log.info(`format ${name} blocked.`)
                formatMaster.setBlockedByPublisher(true);
            }
        }
    }

    public resetFormatBlocks(): void {
        for (const [/*name*/, formatMaster] of Object.entries(this.formats)) {
            formatMaster.setBlockedByPublisher(false);
        }
    }
}
